import React, { useCallback, useMemo } from "react";

import { useWatch } from "react-hook-form";
import { Link, useLocation } from "react-router-dom";

import { TaskActivityLog } from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskActivityLog";
import { TaskComments } from "@fartherfinance/frontend/api/Tasks/hooks/useGetTaskComments";
import {
  Attachment,
  TaskDetailsV3,
} from "@fartherfinance/frontend/api/Tasks/Types";
import { useUpdateTask } from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useUpdateTask";
import { TaskDetails } from "@fartherfinance/frontend/api/TaskWorkflows/Types";

import TaskActivityV2 from "../../Tasks/TaskDetails/Components/TaskActivity/TaskActivityV2";
import TaskAttachments from "../../Tasks/TaskDetails/Components/TaskAttachments/TaskAttachments";
import { TaskPermissionsProvider } from "../../Tasks/taskPermissionsContext";
import { FormEngine, FormEngineProvider } from "../FormEngine";
import DynamicTitle from "../FormEngine/components/DynamicTitle";
import {
  useFormMethodsContext,
  useTemplateContext,
} from "../FormEngine/context";
import { FormData } from "../FormEngine/core/types";
import { mapFormDataToUpdateRequest } from "../FormEngine/utils/mappers";
import TaskSubtasksWorkflow from "../TaskSubtasksWorkflow/TaskSubtasksWorkflow";
import TaskWorkflowActions from "../TaskWorkflowActions/TaskWorkflowActions";
import useAdvisorRequestAuth from "@src/multiCustodian/hooks/useAdvisorRequestAuth";
import useStatusNotification from "@src/multiCustodian/hooks/useStatusNotification";
import Text from "@src/sharedComponents/Typography/Text";
import { extractAxiosErrorMessage } from "@src/utils/axios";

import styles from "./TaskDetailsWorkflow.module.css";

interface CreateTaskLocationState {
  type: "createTask";
  data: {
    taskType?: string;
    taskTypeId?: number;
    client?: string;
    taskTypeTemplate?: string;
  };
}

interface SubtaskLocationState {
  type: "subtask";
  taskDetails: TaskDetails;
}

type LocationState = CreateTaskLocationState | SubtaskLocationState | undefined;

interface FormSubmissionHandlerProps {
  taskId: string;
  taskWorkflowData: TaskDetails | undefined;
  setIsDrawerOpen: (open: boolean) => void;
  setIsMutating: (mutating: boolean) => void;
}

const FormSubmissionHandler: React.FC<FormSubmissionHandlerProps> = ({
  taskId,
  taskWorkflowData,
  setIsDrawerOpen,
  setIsMutating,
}) => {
  const auth = useAdvisorRequestAuth();

  if (!auth) {
    throw new Error(
      "Authentication required for FormSubmissionHandler - TaskWorkflows"
    );
  }

  const { currentTemplate } = useTemplateContext();
  const { formMethods } = useFormMethodsContext();
  const statusNotification = useStatusNotification();
  const updateTaskMutation = useUpdateTask(auth);

  const handleFormSubmit = useCallback(
    async (formData: FormData): Promise<void> => {
      if (!currentTemplate?.templateId) {
        statusNotification("Template not loaded. Please try again.", "Error");
        return;
      }

      try {
        setIsMutating(true);

        // Map only dirty fields to API format for selective updates
        const requestData = mapFormDataToUpdateRequest(
          formData,
          formMethods.formState.dirtyFields,
          currentTemplate.templateId
        );

        await updateTaskMutation.mutateAsync({
          taskId,
          requestData,
          // TODO: Add file support for updates if needed
        });

        statusNotification("Task updated.", "Success");
        setIsDrawerOpen(false);
      } catch (error) {
        statusNotification(
          extractAxiosErrorMessage(error, "Failed to update task."),
          "Error"
        );
      } finally {
        setIsMutating(false);
      }
    },
    [
      currentTemplate?.templateId,
      taskId,
      updateTaskMutation,
      statusNotification,
      setIsDrawerOpen,
      setIsMutating,
      formMethods,
    ]
  );

  const externalResources = useMemo(
    () => ({
      taskMeta: {
        isSystemGenerated: taskWorkflowData?.meta?.isSystemGenerated ?? false,
      },
      authConfig: auth,
    }),
    [taskWorkflowData?.meta?.isSystemGenerated, auth]
  );

  return (
    <FormEngine
      onSubmit={handleFormSubmit}
      externalResources={externalResources}
    />
  );
};

const DynamicTitleInHeader: React.FC = () => {
  const { formMethods } = useFormMethodsContext();
  const { control } = formMethods;

  const titleValue = useWatch({
    control,
    name: "title",
  }) as string | undefined;

  const titleValueString = typeof titleValue === "string" ? titleValue : "";

  return <DynamicTitle title={titleValueString} />;
};

interface TaskDetailsWorkflowUIProps {
  taskWorkflowData: TaskDetails;
  taskDetailsV3: TaskDetailsV3;
  taskAttachments: Attachment[];
  taskComments: TaskComments["values"];
  taskActivities: TaskActivityLog["data"];
  setIsDrawerOpen: (open: boolean) => void;
  setIsMutating: (mutating: boolean) => void;
  isEmbedded: boolean;
}

const TaskDetailsWorkflowUI: React.FC<TaskDetailsWorkflowUIProps> = ({
  taskWorkflowData,
  taskDetailsV3,
  taskAttachments,
  taskComments,
  taskActivities,
  setIsDrawerOpen,
  setIsMutating,
  isEmbedded,
}) => {
  const location = useLocation<LocationState>();

  const initialValues = useMemo(() => {
    const locationState = location.state;

    let result;
    if (locationState?.type === "subtask") {
      result = locationState.taskDetails.fields;
    } else if (locationState?.type === "createTask") {
      const { data } = locationState;
      result = {
        taskType: data.taskType,
        client: data.client,
      };
    } else {
      // For normal task detail flow, return task fields when available
      result = taskWorkflowData?.fields ?? undefined;
    }

    return result;
  }, [location.state, taskWorkflowData?.fields]);

  return (
    <TaskPermissionsProvider
      comments={taskComments}
      attachments={taskAttachments}
      taskId={taskWorkflowData.id}
    >
      <FormEngineProvider
        key={`form-engine-${taskWorkflowData.id}`}
        initialValues={initialValues}
      >
        {/* Header Section - Parent Task Link, Dynamic Title and Actions */}
        <div className={styles.headerSection}>
          <div className={styles.parentTaskContainer}>
            <div className={styles.titleAndActionsRow}>
              <DynamicTitleInHeader />
              <TaskWorkflowActions taskDetails={taskWorkflowData} />
            </div>
            {taskWorkflowData.parent && (
              <Link
                className={styles.parentTaskLink}
                to={{
                  pathname: `/Advisor/Tasks/${taskWorkflowData.parent.id}`,
                  search: location.search,
                }}
              >
                <Text variant="body" color="textSubtle">
                  Parent Task: {taskWorkflowData.parent.title}
                </Text>
              </Link>
            )}
          </div>
        </div>

        {/* Form Engine Section */}
        <div className={styles.formSection}>
          <FormSubmissionHandler
            taskId={taskWorkflowData.id}
            taskWorkflowData={taskWorkflowData}
            setIsDrawerOpen={setIsDrawerOpen}
            setIsMutating={setIsMutating}
          />
        </div>
      </FormEngineProvider>

      {/* Subtasks Section */}
      {taskWorkflowData && (
        <div className={styles.subtasksSection}>
          <TaskSubtasksWorkflow taskWorkflowData={taskWorkflowData} />
        </div>
      )}

      {/* Attachments Section */}
      {taskAttachments.length > 0 && (
        <div className={styles.attachmentsSection}>
          <TaskAttachments
            taskId={taskWorkflowData.id}
            attachments={taskAttachments}
          />
        </div>
      )}

      {/* Activity Section */}
      <div className={styles.activitySection}>
        <TaskActivityV2
          taskDetails={taskDetailsV3}
          taskActivities={taskActivities}
          isEmbedded={isEmbedded}
        />
      </div>
    </TaskPermissionsProvider>
  );
};

export default TaskDetailsWorkflowUI;
